import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { formatPlayCount } from '../lib/utils';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };

const Leaderboard = () => {
  const { profile, user } = useAuth();
  const [hunters, setHunters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [currentUserRank, setCurrentUserRank] = useState(null);

  useEffect(() => { loadCampaigns(); }, []); // eslint-disable-line

  useEffect(() => {
    if (selectedCampaign) loadHunters(selectedCampaign);
    else loadAllHunters();
  }, [selectedCampaign]); // eslint-disable-line

  const loadCampaigns = async () => {
    try {
      const data = await api.get('/api/campaigns');
      setCampaigns(Array.isArray(data) ? data : []);
    } catch { setCampaigns([]); }
  };

  const processHunters = (data) => {
    const sorted = (Array.isArray(data) ? data : []).sort((a, b) => (b.total_play_count || 0) - (a.total_play_count || 0));
    setHunters(sorted);
    const userId = user?.id || profile?.user_id;
    if (userId) {
      const rank = sorted.findIndex(h => h.user_id === userId) + 1;
      setCurrentUserRank(rank > 0 ? rank : null);
    }
  };

  const loadAllHunters = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/leaderboard');
      processHunters(data);
    } catch (error) {
      alert('Error loading leaderboard: ' + error.message);
      setHunters([]);
    } finally { setLoading(false); }
  };

  const loadHunters = async (campaignId) => {
    setLoading(true);
    try {
      const data = await api.get(`/api/leaderboard?campaign_id=${campaignId}`);
      processHunters(data);
    } catch (error) {
      alert('Error loading leaderboard: ' + error.message);
      setHunters([]);
    } finally { setLoading(false); }
  };

  const isCurrentUser = (hunter) => {
    const userId = user?.id || profile?.user_id;
    return hunter.user_id === userId;
  };

  const avatarBg = (rank) => {
    if (rank === 1) return 'bg-amber-100 text-amber-700';
    if (rank === 2) return 'bg-gray-100 text-gray-600';
    if (rank === 3) return 'bg-orange-100 text-orange-600';
    return 'bg-[#ecf8fb] text-[#2a798c]';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Leaderboard</h1>
          <p className="text-[13px] text-[#8a8a8a] mt-0.5">Ranking berdasarkan total views</p>
        </div>
        {currentUserRank && (
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-[13px] font-semibold bg-[#ecf8fb] text-[#2a798c] border border-[#3fb5d2]/30">
            Peringkat Anda: #{currentUserRank}
          </span>
        )}
      </div>

      {/* Campaign filter */}
      <div className="bg-white rounded-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" style={card}>
        <span className="text-[13px] font-medium text-[#646464] flex-shrink-0">Filter:</span>
        <select
          value={selectedCampaign}
          onChange={(e) => setSelectedCampaign(e.target.value)}
          className="px-3 py-1.5 text-[13px] border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3fb5d2] text-[#3b3b3b] bg-white w-full min-w-0 sm:min-w-[200px] sm:w-auto max-w-full"
        >
          <option value="">All Campaigns</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {hunters.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center" style={card}>
          <p className="text-[14px] text-[#8a8a8a] font-medium">Belum ada data leaderboard</p>
          <p className="text-[12px] text-[#8a8a8a] mt-1">
            {selectedCampaign ? 'Tidak ada hunter di campaign ini.' : 'Berpartisipasi dalam campaign untuk muncul di leaderboard!'}
          </p>
        </div>
      ) : (
        <>
          {/* Top 3 podium — desktop only */}
          {hunters.length >= 2 && (
            <div className="hidden md:flex items-end justify-center gap-4 py-4">
              {/* 2nd */}
              {hunters[1] && (
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-[20px] font-bold text-gray-600 mb-2">
                    {hunters[1].full_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="text-[28px] mb-1">🥈</div>
                  <p className="text-[13px] font-semibold text-[#3b3b3b]">{hunters[1].full_name || 'Unknown'}</p>
                  <p className="text-[12px] text-[#8a8a8a]">{formatPlayCount(hunters[1].total_play_count || 0)} views</p>
                  {hunters[1].tier && <p className="text-[11px] text-[#3fb5d2] font-medium mt-0.5">{hunters[1].tier.nama}</p>}
                </div>
              )}
              {/* 1st */}
              {hunters[0] && (
                <div className="flex flex-col items-center -mt-4">
                  <div className="w-18 h-18 rounded-full bg-amber-100 flex items-center justify-center text-[22px] font-bold text-amber-700 mb-2 ring-4 ring-amber-300/50"
                    style={{ width: 72, height: 72 }}>
                    {hunters[0].full_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="text-[36px] mb-1">🥇</div>
                  <p className="text-[14px] font-bold text-[#3b3b3b]">{hunters[0].full_name || 'Unknown'}</p>
                  <p className="text-[13px] font-semibold text-amber-600">{formatPlayCount(hunters[0].total_play_count || 0)} views</p>
                  {hunters[0].tier && <p className="text-[11px] text-[#3fb5d2] font-medium mt-0.5">{hunters[0].tier.nama}</p>}
                </div>
              )}
              {/* 3rd */}
              {hunters[2] && (
                <div className="flex flex-col items-center mt-4">
                  <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center text-[20px] font-bold text-orange-600 mb-2">
                    {hunters[2].full_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="text-[28px] mb-1">🥉</div>
                  <p className="text-[13px] font-semibold text-[#3b3b3b]">{hunters[2].full_name || 'Unknown'}</p>
                  <p className="text-[12px] text-[#8a8a8a]">{formatPlayCount(hunters[2].total_play_count || 0)} views</p>
                  {hunters[2].tier && <p className="text-[11px] text-[#3fb5d2] font-medium mt-0.5">{hunters[2].tier.nama}</p>}
                </div>
              )}
            </div>
          )}

          {/* Full list */}
          <div className="bg-white rounded-2xl overflow-hidden" style={card}>
            <div className="divide-y divide-[#e5e5e5]">
              {hunters.map((hunter, index) => {
                const rank = index + 1;
                const isUser = isCurrentUser(hunter);
                const maxViews = hunters[0]?.total_play_count || 1;
                const pct = Math.min(((hunter.total_play_count || 0) / maxViews) * 100, 100);

                return (
                  <div key={hunter.user_id}
                    className={`px-4 py-3.5 flex items-center gap-3 hover:bg-gray-50/50 transition-colors ${isUser ? 'bg-[#ecf8fb]/60 border-l-2 border-[#3fb5d2]' : ''}`}>
                    {/* Rank */}
                    <div className="w-8 flex-shrink-0 text-center">
                      {rank === 1 ? <span className="text-[20px]">🥇</span>
                        : rank === 2 ? <span className="text-[20px]">🥈</span>
                        : rank === 3 ? <span className="text-[20px]">🥉</span>
                        : <span className="text-[13px] font-bold text-[#8a8a8a]">#{rank}</span>}
                    </div>

                    {/* Avatar */}
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[14px] font-bold flex-shrink-0 ${avatarBg(rank)}`}>
                      {hunter.full_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[13px] font-semibold ${isUser ? 'text-[#2a798c]' : 'text-[#3b3b3b]'}`}>
                          {hunter.full_name || 'Unknown'}
                        </span>
                        {isUser && (
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#3fb5d2] text-white">Anda</span>
                        )}
                        {hunter.tier && (
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#ecf8fb] text-[#2a798c] border border-[#3fb5d2]/30">
                            {hunter.tier.nama}
                          </span>
                        )}
                      </div>
                      {/* Progress bar */}
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1.5 bg-[#e5e5e5] rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${rank === 1 ? 'bg-amber-400' : rank === 2 ? 'bg-gray-400' : rank === 3 ? 'bg-orange-400' : 'bg-[#3fb5d2]'}`}
                            style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[12px] font-semibold text-[#3b3b3b] whitespace-nowrap">
                          {formatPlayCount(hunter.total_play_count || 0)} views
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Leaderboard;
